import { Features } from '../../../../../../../constants/features';
import { messages } from './messages';

/**
 * @typedef {import('../../new-lineup-plan-grid-types.d.ts').FeatureGroupDefinition} FeatureGroupDefinition
 */

/**
 * @type {FeatureGroupDefinition[]}
 */
export const featureGroupDefinitions = [
  {
    id: 'orderManagement',
    nameMessageDescriptor: messages.orderManagement,
    features: [
      {
        id: Features.ManualOrderCreation,
        messageDescriptor: messages.manualOrderCreation,
      },
      {
        id: Features.OrderImportBasic,
        messageDescriptor: messages.orderImportCSV,
      },
      {
        id: Features.OrderImportCustomStore,
        messageDescriptor: messages.orderImportCustom,
      },
      {
        id: Features.CombineSplitOrdersShipments,
        messageDescriptor: messages.combineSplitOrders,
      },
    ],
  },
  {
    id: 'shipping',
    nameMessageDescriptor: messages.shipping,
    features: [
      {
        id: Features.AddressValidation,
        messageDescriptor: messages.addressValidation,
      },
      {
        id: Features.DiscountedCarrierRates,
        messageDescriptor: messages.discountedCarrierRates,
      },
      {
        id: Features.InsurancePurchase,
        messageDescriptor: messages.insurancePurchase,
      },
      {
        id: Features.NonWalletProviderAccess,
        messageDescriptor: messages.byoa,
      },
    ],
  },
  {
    id: 'returns',
    nameMessageDescriptor: messages.returns,
    features: [
      {
        id: Features.LegacyBrandedReturns,
        messageDescriptor: messages.standardLegacyReturnsPortal,
      },
      {
        id: Features.LegacyReturnLabels,
        messageDescriptor: messages.manualReturnLabels,
      },
      {
        id: Features.MerchantManagedReturnsPortal,
        messageDescriptor: messages.merchantManagedReturnsPortal,
      },
      {
        id: Features.BrandedReturnsExchangePortal,
        messageDescriptor: messages.brandedReturnsExchangePortal,
      },
    ],
  },
  {
    id: 'tracking',
    nameMessageDescriptor: messages.tracking,
    features: [
      {
        id: Features.TrackingCustomerNotificationEmails,
        messageDescriptor: messages.customerNotificationEmails,
      },
      {
        id: Features.BrandedDomains,
        messageDescriptor: messages.brandedNotificationEmails,
      },
      {
        id: Features.BrandedTracking,
        messageDescriptor: messages.brandedTracking,
      },
    ],
  },
  {
    id: 'automation',
    nameMessageDescriptor: messages.automation,
    features: [
      {
        id: Features.CommonAutomationRules,
        messageDescriptor: messages.labelGenAutomationRules,
      },
      {
        id: Features.UnlimitedAutomationRules,
        messageDescriptor: messages.unlimitedAutomationRules,
      },
      {
        id: Features.FreemiumRateShopper,
        messageDescriptor: messages.automatedRateShopper,
      },
      {
        id: Features.ShippingStrategies,
        messageDescriptor: messages.customizedRateShopper,
      },
      { id: Features.AutoSplit, messageDescriptor: messages.autoSplit },
    ],
  },
  {
    id: 'scale',
    nameMessageDescriptor: messages.scale,
    features: [
      {
        id: Features.PurchaseAdditionalUsers,
        messageDescriptor: messages.purchaseAdditionalUsers,
      },
      {
        id: Features.UserRoleManagementFeature,
        messageDescriptor: messages.userPermissions,
      },
      { id: Features.GoogleSSO, messageDescriptor: messages.googleSSO },
      { id: Features.ShipStationApiV2, messageDescriptor: messages.apiAccess },
    ],
  },
  {
    id: 'dataAndReporting',
    nameMessageDescriptor: messages.dataAndReporting,
    features: [
      { id: Features.DataExport, messageDescriptor: messages.dataExport },
      {
        id: Features.ReportsViewOnly,
        messageDescriptor: messages.reportsViewOnly,
      },
      {
        id: Features.ReportsDownload,
        messageDescriptor: messages.reportsDownload,
      },
      {
        id: Features.DashboardsViewOnly,
        messageDescriptor: messages.dashboardsViewOnly,
      },
      { id: Features.ODBC, messageDescriptor: messages.odbc },
    ],
  },
  {
    id: 'inventory',
    nameMessageDescriptor: messages.inventory,
    features: [
      {
        id: Features.ProductCatalog,
        messageDescriptor: messages.productCatalog,
      },
      {
        id: Features.InventoryManagementBasic,
        messageDescriptor: messages.inventoryGrid,
      },
      { id: Features.ProductBundles, messageDescriptor: messages.bundles },
      { id: Features.LotTracking, messageDescriptor: messages.lotTracking },
      { id: Features.InventorySync, messageDescriptor: messages.inventoryTrackingSync },
      { id: Features.ReorderAssist, messageDescriptor: messages.reorderAssist },
      { id: Features.PurchaseOrders, messageDescriptor: messages.purchaseOrders },
      { id: Features.ScanToReceive, messageDescriptor: messages.scanToReceive },
    ],
  },
  {
    id: 'warehouseManagement',
    nameMessageDescriptor: messages.warehouseManagement,
    features: [
      { id: Features.PickLists, messageDescriptor: messages.pickLists },
      { id: Features.PackingSlips, messageDescriptor: messages.packingSlips },
      {
        id: Features.PackingSlipsCustomized,
        messageDescriptor: messages.packingSlipsCustomized,
      },
      {
        id: Features.BarcodeScanning,
        messageDescriptor: messages.barcodeScanning,
      },
      { id: Features.HotKeys, messageDescriptor: messages.hotKeys },
      { id: Features.MobilePicking, messageDescriptor: messages.mobilePicking },
      { id: Features.PickingStatuses, messageDescriptor: messages.pickingStatuses },
      { id: Features.PickToTote, messageDescriptor: messages.pickToTote },
      { id: Features.Cubiscan, messageDescriptor: messages.cubiscan },
    ],
  },
  {
    id: 'support',
    nameMessageDescriptor: messages.support,
    features: [
      {
        id: Features.EmailCommunityForumHelpLibrary,
        messageDescriptor: messages.chatbotForumHelpLibrary,
      },
      { id: Features.EmailSupport, messageDescriptor: messages.emailSupport },
      // Using email support feature id for chat support for now. The current chat feature is enabled for all plans to
      // allow the use of the chatbot for everyone. We don't configure the bot vs live agent experience on our end, that
      // is something done by support.
      { id: Features.EmailSupport, messageDescriptor: messages.chatSupport },
      { id: Features.PhoneSupport, messageDescriptor: messages.phoneSupport },
      {
        id: Features.DedicatedAccountManager,
        messageDescriptor: messages.dedicatedAccountManagement,
      },
    ],
  },
  {
    id: 'otherIntegrations',
    nameMessageDescriptor: messages.otherIntegrationsCategory,
    features: [
      {
        id: Features.QuickbooksIntegration,
        messageDescriptor: messages.quickBooksIntegration,
      },
      {
        id: Features.WebhooksIntegration,
        messageDescriptor: messages.webhooksIntegration,
      },
      // using OtherIntegrations feature id for Return Rabbit integration. There is a difference between the integrated
      // RR stuff and external integration, and we never made a separate feature for the external integration.
      {
        id: Features.OtherIntegrations,
        messageDescriptor: messages.returnRabbit,
      },
      {
        id: Features.GoogleIntegration,
        messageDescriptor: messages.googleIntegration,
      },
      {
        id: Features.MailChimpIntegration,
        messageDescriptor: messages.mailchimpIntegration,
      },
      {
        id: Features.ZapierIntegration,
        messageDescriptor: messages.zapierIntegration,
      },
      {
        id: Features.OtherIntegrations,
        messageDescriptor: messages.otherIntegrationsFeature,
      },
    ],
  },
];
